import { markRaw } from 'vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import { useInstitutionConnect } from '@/hooks/institutionConnect'

import { useAIAssetWizardStore } from '@/store/ai/assetWizard'
import { useModalsStore } from '@/store/modals'

import ConnectorOptions from '@/modules/Connectors/ConnectorOptions.vue'
import WizardDialog from '@/modules/AddAsset/Wizards/WizardDialog.vue'
import AddAssetDialog from '@/modules/AddAsset/AddAssetDialog.vue'

const noop = () => {}

type Options = {
  /**
   * Hide the surface the user is dispatching from, so a hand-off dialog
   * (instructions, Akoya, Plaid Link, Yodlee FastLink, the wizard) is not
   * stacked on top of it. Only meaningful for a caller that is itself a
   * dialog — an inline embed has nothing to hide and leaves this at its
   * no-op default.
   */
  hide?: () => void
  /** Bring that surface back when the user cancels the hand-off. */
  show?: () => void
  /** Ends the whole flow, for a caller that is itself a dialog to close. */
  onDismiss?: () => void
}

/**
 * The dispatch behind AddAssetConnect / AddAssetCompose: pick an institution,
 * describe an asset in free text, or jump straight into one wizard's flow.
 *
 * Extracted from AddAssetDialog so the @ds "Add asset" dialog (opened from
 * the nav) and an inline embed of the same connect/compose pair (the
 * Welcome screen's empty state) dispatch identically. Neither owns it.
 */
export const useAddAssetFlow = ({ hide = noop, show = noop, onDismiss }: Options = {}) => {
  const assetWizardStore = useAIAssetWizardStore()
  const modalsStore = useModalsStore()

  const { loading, connectInstitution } = useInstitutionConnect({ hide, show })

  const handleClickAddAccount = () => {
    const modalInstance = modalsStore.init('add-account', markRaw(AddAssetDialog))
    modalInstance?.open(modalsStore.getZIndex())
  }

  const handleMoreOptions = () => {
    hide()
    const modalInstance = modalsStore.init(
      'add-asset-connectors',
      markRaw(ConnectorOptions),
    )
    modalInstance?.open(modalsStore.getZIndex(), { onBack: show, onDismiss })
  }

  /*
   * Continue hands the sentence to the assistant and opens the summary dialog
   * straight away — the read takes a moment, and the wait belongs on the screen
   * that will show its result rather than on this one.
   *
   * The answer is not passed as a prop: the summary reads it off the store, so a
   * retry or a re-read from in there lands in the same place as the first read.
   */
  const handleDescribe = (sentence: string) => {
    hide()

    assetWizardStore.submit(sentence)

    const modalInstance = modalsStore.init('add-asset-wizard', markRaw(WizardDialog))
    modalInstance?.open(modalsStore.getZIndex(), { onBack: show, onDismiss })
  }

  const handlePickFlow = (flow: AssetWizardFlow) => {
    hide()

    const modalInstance = modalsStore.init('add-asset-wizard', markRaw(WizardDialog))
    modalInstance?.open(modalsStore.getZIndex(), { onBack: show, onDismiss, flow })
  }

  return {
    loading,

    handleConnect: connectInstitution,
    handleClickAddAccount,
    handleMoreOptions,
    handleDescribe,
    handlePickFlow,
  }
}
